// Atlas waters-DRILL — drill-down waters filter with in-place (AJAX) filtering.
//
// Progressive enhancement only. Every pill is a real Shopify filter link, so
// with JS off (or reduced motion on) the browser just navigates and the server
// renders the right level.
//
// With JS on we avoid the full-page reload entirely: the click plays its
// transition, we fetch the filtered URL in the background, then swap in the new
// waters component + product grid + count and push the URL. No flash, and the
// drilled controls (back link, selected group) are already correct in the
// swapped markup rather than popping in after a reload.
//
// Which transitions animate:
//   • group  (top → drilled)      slide the pill into the slot, regions fade in
//   • back   (drilled → top)      drilled controls fall away
//   • region / selected (staying inside the same group) no motion: it is the
//     same level, so replaying the entry animation reads as a glitch.
(function () {
  var REDUCED = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var GRID = "#ProductGridContainer";
  var COUNT = ".atlas-typebar__count";

  function slice(nodes) {
    return Array.prototype.slice.call(nodes);
  }

  function isNarrow() {
    return window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
  }

  function currentRoot() {
    return document.querySelector("[data-wdrill]");
  }

  // Fetch the filtered page and swap the pieces that changed.
  function swapTo(href, after) {
    fetch(href)
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");

        var newDrill = doc.querySelector("[data-wdrill]");
        var oldDrill = currentRoot();
        if (newDrill && oldDrill) oldDrill.replaceWith(newDrill);

        var newGrid = doc.querySelector(GRID);
        var oldGrid = document.querySelector(GRID);
        if (newGrid && oldGrid) oldGrid.replaceWith(newGrid);

        var newCount = doc.querySelector(COUNT);
        var oldCount = document.querySelector(COUNT);
        if (newCount && oldCount) oldCount.replaceWith(newCount);

        window.history.pushState({ wdrill: true }, "", href);
        if (typeof after === "function") after();
      })
      .catch(function () {
        window.location.assign(href); // network hiccup: fall back to a normal load
      });
  }

  // TOP → DRILLED: promote the chosen group pill into the All-waters slot, then
  // swap once the motion has played.
  function promote(root, pill, href) {
    var row = root.querySelector(".wdrill__row");
    var allPill = root.querySelector("[data-wdrill-all]");
    var back = root.querySelector("[data-wdrill-back]");
    var groups = slice(root.querySelectorAll("[data-wdrill-group]"));
    if (!row) return swapTo(href);

    if (isNarrow()) {
      // wrapped rows make a horizontal slide meaningless; quick crossfade
      row.classList.add("is-fading");
      setTimeout(function () {
        swapTo(href);
      }, 170);
      return;
    }

    row.classList.add("is-anim");

    // Measure everything before touching the DOM.
    var rowRect = row.getBoundingClientRect();
    var slotRect = (allPill || pill).getBoundingClientRect();
    var pillRect = pill.getBoundingClientRect();
    var gap = parseFloat(window.getComputedStyle(row).columnGap) || 8;

    // The drilled state opens with a "‹ All waters" back link that the top
    // state has no room for, and the selected group sits AFTER it — so the
    // pill's real landing spot is past the back link, not in the All-waters
    // slot. Bring the back link in as part of this same motion, parked out of
    // flow in the slot All-waters is vacating (so revealing it reflows
    // nothing), and aim the pill at where it will actually come to rest. Both
    // pieces are then already in place when the swapped markup arrives.
    var backWidth = 0;
    if (back) {
      back.classList.add("is-arriving");
      back.style.left = slotRect.left - rowRect.left + "px";
      back.style.top = slotRect.top - rowRect.top + "px";
      backWidth = back.getBoundingClientRect().width;
      requestAnimationFrame(function () {
        back.classList.add("is-here");
      });
    }

    var dx = slotRect.left - pillRect.left;
    if (backWidth) dx += backWidth + gap;

    if (allPill) allPill.classList.add("is-leaving");
    groups.forEach(function (other) {
      if (other !== pill) other.classList.add("is-leaving");
    });

    // Take on the selected chip's shape NOW rather than inheriting it from the
    // swap: drop the disclosure caret and grow the remove-✕. Fading the caret
    // alone wasn't enough — its box still held ~12rem/10 of space, so the pill
    // visibly snapped narrower and lost the caret the instant the new markup
    // landed. Doing it up front, while the pill is already turning brass, reads
    // as the group becoming the applied filter.
    if (!pill.querySelector(".wchip__x")) {
      var x = document.createElement("span");
      x.className = "wchip__x";
      x.setAttribute("aria-hidden", "true");
      x.textContent = "✕";
      pill.appendChild(x);
    }

    pill.classList.add("is-promoting");
    requestAnimationFrame(function () {
      pill.style.transform = "translateX(" + dx + "px)";
    });

    var rsId = pill.getAttribute("data-regionset");
    var regionset = rsId ? root.querySelector('[data-wdrill-regionset="' + rsId + '"]') : null;
    if (regionset) {
      regionset.hidden = false;
      regionset.classList.add("is-entering");
    }

    // swap after the slide has read, so the promoted pill lands where the
    // server-rendered selected chip already sits: no visible jump.
    setTimeout(function () {
      swapTo(href);
    }, 380);
  }

  // DRILLED → TOP: let the drilled controls fall away, then swap.
  function reverse(root, href) {
    var row = root.querySelector(".wdrill__row");
    if (!row) return swapTo(href);

    if (isNarrow()) {
      row.classList.add("is-fading");
      setTimeout(function () {
        swapTo(href);
      }, 170);
      return;
    }

    row.classList.add("is-anim", "is-reversing");
    setTimeout(function () {
      swapTo(href);
    }, 300);
  }

  // Delegated so it keeps working after the component is swapped out.
  document.addEventListener("click", function (event) {
    if (REDUCED) return; // native navigation renders the level
    var el = event.target.closest
      ? event.target.closest(
          "[data-wdrill-group], [data-wdrill-back], [data-wdrill-selected], .wdrill__region"
        )
      : null;
    if (!el) return;
    var root = el.closest("[data-wdrill]");
    if (!root) return;
    var href = el.getAttribute("href");
    if (!href) return;

    // The in-place swap only replaces the waters row, the product grid and the
    // count — everything else on the page (collection title, hero, breadcrumb)
    // still belongs to the collection we're on. So a link that leaves this
    // collection has to be a real navigation, or you'd end up reading
    // "Michigan Wood Maps" above the whole catalogue. "All waters" now points
    // at /collections/all, which is exactly that case.
    var target = new URL(href, window.location.href);
    if (target.pathname !== window.location.pathname) return;

    event.preventDefault();

    if (el.hasAttribute("data-wdrill-group")) {
      promote(root, el, href);
    } else if (el.hasAttribute("data-wdrill-back")) {
      reverse(root, href);
    } else {
      // region pill, or the selected group chip (broaden/clear): same level,
      // so swap straight in without replaying an entry animation.
      swapTo(href);
    }
  });

  // Browser back/forward should re-sync the same way.
  window.addEventListener("popstate", function () {
    if (currentRoot()) swapTo(window.location.href);
  });
})();
